import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { useSiteConfig } from '@/context/SiteConfigContext';

// Mobile Menu as a separate component with portal
const MobileMenu = ({ isOpen, onClose, navLinks, location }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="lg:hidden">
      {/* Overlay */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        style={{ zIndex: 9998 }}
        onClick={onClose}
      />
      
      {/* Menu Panel */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="fixed top-0 right-0 w-80 max-w-[85vw] bg-white overflow-y-auto shadow-2xl"
        style={{ zIndex: 9999, height: '100vh' }}
        data-testid="mobile-menu"
      >
        {/* Header with logo */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <Link to="/" onClick={onClose} className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_tech5-recruitment/artifacts/b0zdbykt_IMG_3384.jpg"
              alt="Tech5sages"
              className="h-10 w-10 object-contain rounded-lg"
            />
            <span className="text-lg font-bold bg-gradient-to-r from-[#0EA5E9] to-[#1E3A5F] bg-clip-text text-transparent">
              Tech5sages
            </span>
          </Link>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="px-4 py-6 space-y-2">
          {navLinks.map((link, index) => (
            <motion.div
              key={link.path}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link
                to={link.path}
                className={`flex items-center justify-between py-3 px-4 rounded-xl text-base font-medium transition-all duration-300 ${
                  location.pathname === link.path 
                    ? 'text-white bg-gradient-to-r from-[#0EA5E9] to-[#1E3A5F]' 
                    : 'text-gray-700 hover:bg-[#0EA5E9]/10 hover:text-[#0EA5E9]'
                }`}
                onClick={onClose}
              >
                {link.name}
              </Link>
            </motion.div>
          ))}
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="pt-6 space-y-3 border-t border-gray-100 mt-4"
          >
            <Link to="/refer" onClick={onClose}>
              <Button variant="outline" className="w-full py-3 border-[#0EA5E9]/30 text-[#0EA5E9] hover:bg-[#0EA5E9]/10 rounded-xl">
                Refer & Earn
              </Button>
            </Link>
            <Link to="/apply" onClick={onClose}>
              <Button className="w-full py-3 bg-gradient-to-r from-[#0EA5E9] to-[#1E3A5F] text-white font-bold rounded-xl shadow-lg">
                Get Started
              </Button>
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isPageVisible } = useSiteConfig();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Navigation links - Simple, no dropdowns
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Careers', path: '/jobs' },
    { name: 'Culture', path: '/culture' },
    { name: 'Contact', path: '/contact' },
    { name: 'About', path: '/about' },
  ].filter(link => {
    const pageName = link.path === '/' ? 'home' : 
                    link.path === '/jobs' ? 'jobs' : 
                    link.path.replace('/', '');
    return isPageVisible(pageName);
  });

  // Mobile nav links
  const mobileNavLinks = navLinks.map(link => ({
    name: link.name,
    path: link.path
  }));

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-lg shadow-[#0EA5E9]/5' 
            : 'bg-white'
        }`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo & Company Name */}
            <Link to="/" className="flex items-center gap-3 z-50 group" data-testid="logo-link">
              {/* Logo Icon */}
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_tech5-recruitment/artifacts/b0zdbykt_IMG_3384.jpg"
                  alt="Tech5sages"
                  className="h-12 w-12 object-contain rounded-xl shadow-md transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              
              {/* Company Name - Prominent Display */}
              <div className="flex flex-col">
                <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#0EA5E9] to-[#1E3A5F] bg-clip-text text-transparent leading-tight tracking-tight">
                  Tech5sages Global
                </span>
                <span className="text-[10px] sm:text-xs text-gray-500 font-medium tracking-widest uppercase">
                  Pvt. Ltd.
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'text-[#0EA5E9] bg-[#0EA5E9]/10'
                      : 'text-gray-600 hover:text-[#0EA5E9] hover:bg-[#0EA5E9]/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/refer">
                <Button 
                  variant="outline"
                  className="px-5 py-2 border-[#0EA5E9]/30 text-[#0EA5E9] hover:bg-[#0EA5E9]/10 font-medium rounded-full transition-all duration-300"
                >
                  Refer & Earn
                </Button>
              </Link>
              <Link to="/apply">
                <Button 
                  data-testid="apply-now-button" 
                  className="px-6 py-2.5 bg-gradient-to-r from-[#0EA5E9] to-[#1E3A5F] hover:from-[#0284C7] hover:to-[#1E3A5F] text-white font-semibold rounded-full transition-all duration-300 shadow-lg shadow-[#0EA5E9]/25 hover:shadow-xl hover:shadow-[#0EA5E9]/30"
                >
                  Get Started
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden relative w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-r from-[#0EA5E9]/10 to-[#1E3A5F]/10 text-[#0EA5E9] z-50 transition-all duration-300 hover:from-[#0EA5E9]/20 hover:to-[#1E3A5F]/20"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="h-5 w-5" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="h-5 w-5" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Portal */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
            navLinks={mobileNavLinks}
            location={location}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
